import React, { useState, useEffect, useCallback } from 'react';
import { ChatMessageArea, Message } from './ChatMessageArea';
import { ChatInputArea } from './ChatInputArea';
import { TopicRatingDialog } from './TopicRatingDialog';
import { ImageCropModal } from './ImageCropModal';
import { useSocket, useAIResponse, useTopicActivated, useSocketError } from '../../hooks/useSocket';
import { useStore } from '../../stores';
import { apiService } from '@shared/services/api/ApiService';
import { useLocation } from 'react-router-dom';
import UnifiedMathInput from '../common/UnifiedMathInput';
import { buildStaticUrl } from '../../config/env';
import { getPreferredModel, setPreferredModel, AIModel } from '../../utils/modelPreference';
import { getModelDisplayName } from '../../utils/modelDisplay';
import { ModelSelector } from './ModelSelector';
import { preprocessUserLatex } from '@shared/utils/latexPreprocessor';
import { MembershipWarningModal } from '../teach/MembershipWarningModal';
import { useChatStore } from '../../stores/chatStore';

export interface Topic {
  id: string;
  sessionId: string;
  title: string;
  category: string;
  modelType: AIModel;
  topicType: 'image' | 'text';
  status: 'pending' | 'active' | 'completed' | 'disconnected';
  imageInfo?: {
    imageId: string;
    filename: string;
    fileUrl: string;
  };
  initialQuestion?: string;
}

interface HistoryTopic {
  topicId: string;
  topicType: 'image' | 'text';
  title: string;
  category: string;
  modelType: string;
  status: string;
  messageCount: number;
  createdAt: string;
  imageInfo?: {
    imageId: string;
    filename: string;
    fileUrl: string;
  };
  initialQuestion?: string;
}

/**
 * 聊天界面组件 v2 - 性能优化版本
 * 组件分离，避免输入时重新渲染消息列表
 */
export const ChatInterface: React.FC = () => {
  // 全局聊天状态（路由切换时保持会话）
  const chatStoreState = useChatStore.getState();

  // 基础状态 - 从 chatStore 恢复（如果有已保存的会话）
  const [messages, setMessages] = useState<Message[]>(() =>
    chatStoreState.hasSession ? chatStoreState.messages : []
  );

  const [currentTopic, setCurrentTopic] = useState<Topic | null>(() =>
    chatStoreState.hasSession ? chatStoreState.currentTopic : null
  );
  const [isTyping, setIsTyping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isTopicCompleted, setIsTopicCompleted] = useState(() =>
    chatStoreState.hasSession ? chatStoreState.isTopicCompleted : false
  );
  const [solutionButtonShown, setSolutionButtonShown] = useState<Set<string>>(() =>
    chatStoreState.hasSession ? chatStoreState.solutionButtonShown : new Set()
  );
  const [uploadingType, setUploadingType] = useState<'image' | 'text' | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');

  // 使用记忆的模型偏好，而不是固定的deepseek
  const [selectedModel, setSelectedModel] = useState<AIModel>(() =>
    chatStoreState.hasSession ? chatStoreState.selectedModel : getPreferredModel()
  );
  const [isModelLocked, setIsModelLocked] = useState(() =>
    chatStoreState.hasSession ? chatStoreState.isModelLocked : false
  );

  // 右侧问题输入区域的状态
  const [questionMode, setQuestionMode] = useState<'none' | 'select' | 'image' | 'text'>(() =>
    chatStoreState.hasSession ? chatStoreState.questionMode : 'none'
  );
  const [questionText, setQuestionText] = useState('');
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageAnalyzed, setImageAnalyzed] = useState(() =>
    chatStoreState.hasSession ? chatStoreState.imageAnalyzed : false
  );
  const [textAnalyzed, setTextAnalyzed] = useState(() =>
    chatStoreState.hasSession ? chatStoreState.textAnalyzed : false
  );

  // 历史会话加载状态
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySessionId, setHistorySessionId] = useState<string | null>(() =>
    chatStoreState.hasSession ? chatStoreState.historySessionId : null
  );

  // 评分对话框状态
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [completedTopicId, setCompletedTopicId] = useState<string | null>(null);
  const [completionReason, setCompletionReason] = useState<string | undefined>(undefined);
  const [topicCompletionStatus, setTopicCompletionStatus] = useState<{ [topicId: string]: boolean }>(() =>
    chatStoreState.hasSession ? chatStoreState.topicCompletionStatus : {}
  );

  // 会员状态提醒模态
  const [membershipModal, setMembershipModal] = useState<{
    isOpen: boolean;
    message: string;
    type: 'warning' | 'error';
    qrCodeUrl?: string;
    alreadyBound?: boolean;
  }>({ isOpen: false, message: '', type: 'warning' });

  // 图片裁剪状态
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);

  const { user, token } = useStore();
  const location = useLocation();

  // Socket连接管理
  const {
    connected,
    connecting,
    error: socketError,
    connect,
    disconnect,
    sendMessage,
    joinTopic,
    sessionContinueResponse,
    submitRating,
    on,
    off
  } = useSocket({
    autoConnect: !!token, // 只有当有token时才自动连接
    token: token || undefined
  });

  // 更新连接状态
  useEffect(() => {
    if (connected) {
      setConnectionStatus('connected');
    } else if (connecting) {
      setConnectionStatus('connecting');
    } else {
      setConnectionStatus('disconnected');
    }
  }, [connected, connecting]);

  // 保存模型偏好到localStorage
  useEffect(() => {
    setPreferredModel(selectedModel);
  }, [selectedModel]);

  // 组件卸载时保存会话状态到 chatStore（路由切换时保持会话）
  useEffect(() => {
    return () => {
      // 只在有活跃话题或有消息时保存
      const hasContent = currentTopic !== null || messages.length > 1 || historySessionId !== null;
      if (hasContent) {
        useChatStore.getState().saveSession({
          messages,
          currentTopic,
          historySessionId,
          isTopicCompleted,
          isModelLocked,
          selectedModel,
          solutionButtonShown,
          topicCompletionStatus,
          questionMode,
          imageAnalyzed,
          textAnalyzed,
        });
      }
    };
  });

  // 监听AI响应
  useAIResponse(useCallback((response) => {
    setIsTyping(false);

    const newMessage: Message = {
      id: response.messageId,
      role: 'assistant',
      content: response.message,
      timestamp: new Date(),
      topicId: response.topicId,
      model: response.model as 'gemini' | 'deepseek',
      tokenCount: response.tokenCount,
      responseTime: response.responseTime,
      // 🆕 多模态支持
      messageType: 'text' // AI回复默认为文本类型
    };

    setMessages(prev => [...prev, newMessage]);
  }, []));
  // 监听话题激活
  useTopicActivated(useCallback((data) => {
    setCurrentTopic(prev => {
      if (prev && prev.id === data.topicId) {
        return {
          ...prev,
          status: 'active',
          title: data.title,
          category: data.category,
          modelType: prev.modelType
        };
      }
      return prev;
    });

    setIsModelLocked(true);

    // 不再显示模型选择的系统消息
    // const actualModel = data.modelType || selectedModel;
    // const systemMessage: Message = {
    //   id: `system_${Date.now()}`,
    //   role: 'system',
    //   content: `已选择 AI 模型为您服务`,
    //   timestamp: new Date()
    // };
    // setMessages(prev => [...prev, systemMessage]);
  }, [selectedModel]));

  // 监听Socket错误
  useSocketError(useCallback((error) => {
    console.error('Socket error:', error);

    const errorMessage: Message = {
      id: `error_${Date.now()}`,
      role: 'system',
      content: `❌ 连接错误：${error.message}`,
      timestamp: new Date(),
      messageType: 'text'
    };

    setMessages(prev => [...prev, errorMessage]);
  }, []));

  // 监听其他Socket事件
  useEffect(() => {
    const handleSessionLimitReached = (data: any) => {
      setIsTyping(false);
      const limitMessage: Message = {
        id: `limit_${Date.now()}`,
        role: 'system',
        content: `⚠️ ${data.message}`,
        timestamp: new Date(),
        messageType: 'text'
      };
      setMessages(prev => [...prev, limitMessage]);
    };

    const handleRatingRequest = (data: any) => {
      const ratingMessage: Message = {
        id: `rating_${Date.now()}`,
        role: 'system',
        content: `⭐ ${data.message}`,
        timestamp: new Date(),
        messageType: 'text'
      };
      setMessages(prev => [...prev, ratingMessage]);
    };

    const handleTopicCompleted = (data: any) => {
      console.log('Topic completed from backend:', data);
      const { topicId, completionReason } = data;

      setTopicCompletionStatus(prev => {
        const wasAlreadyCompleted = prev[topicId];

        if (!wasAlreadyCompleted) {
          setIsTopicCompleted(true);
          setCompletedTopicId(topicId);
          setCompletionReason(completionReason || '您已经完成了这个话题的学习');
          setShowRatingDialog(true);

          const completionMessageId = `completion_${Date.now()}`;
          const completionMessage: Message = {
            id: completionMessageId,
            role: 'system',
            content: '🎉 恭喜！您已经完成了这个话题的学习。',
            timestamp: new Date(),
            messageType: 'text'
          };

          setMessages(prevMessages => [...prevMessages, completionMessage]);
          // 实时完成时显示总结按钮
          setSolutionButtonShown(prevButtons => new Set([...prevButtons, completionMessageId]));
        }

        return { ...prev, [topicId]: true };
      });
    };

    const handleSessionDisconnected = (data: any) => {
      console.log('Session disconnected:', data);
      // 更新当前话题状态为已断开
      setCurrentTopic(prev => {
        if (prev) {
          return { ...prev, status: 'disconnected' };
        }
        return prev;
      });
      
      // 显示断开消息
      const disconnectMessage: Message = {
        id: `disconnect_${Date.now()}`,
        role: 'system',
        content: `🔌 ${data.message || '会话已断开'}`,
        timestamp: new Date(),
        messageType: 'text'
      };
      setMessages(prev => [...prev, disconnectMessage]);
    };

    const handleMessageLimitReached = (data: any) => {
      console.log('Message limit reached:', data);
      // 显示评价弹窗
      setShowRatingDialog(true);
      setCompletedTopicId(data.topicId);
      setCompletionReason(data.completionReason);
      
      // 标记完成，封锁输入
      setIsTopicCompleted(true);
    };

    // 🆕 处理超纲警告系统消息
    const handleSystemMessage = (data: any) => {
      console.log('System message received:', data);
      
      if (data.type === 'grade_warning') {
        // 显示超纲警告对话框
        const confirmed = window.confirm(
          `${data.title || '提示'}\n\n${data.message}\n\n点击"确定"继续学习，点击"取消"前往个人中心修改年级。`
        );
        
        if (!confirmed) {
          // 用户选择修改年级，跳转到个人中心
          window.location.href = '/profile';
        }
        // 如果用户选择继续，不做任何操作，继续当前对话
      }
    };

    on('session_limit_reached', handleSessionLimitReached);
    on('rating_request', handleRatingRequest);
    on('topic_completed', handleTopicCompleted);
    on('session_disconnected', handleSessionDisconnected);
    on('message_limit_reached', handleMessageLimitReached);
    on('system_message', handleSystemMessage);

    return () => {
      off('session_limit_reached', handleSessionLimitReached);
      off('rating_request', handleRatingRequest);
      off('topic_completed', handleTopicCompleted);
      off('session_disconnected', handleSessionDisconnected);
      off('message_limit_reached', handleMessageLimitReached);
      off('system_message', handleSystemMessage);
    };
  }, [on, off]);

  // 处理左侧持续对话发送消息
  const handleSendMessage = useCallback(async (message: string) => {
    // 预处理LaTeX表达式用于显示（与数据库存储保持一致）
    const processedMessage = preprocessUserLatex(message);
    
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: processedMessage, // 使用预处理后的内容显示
      timestamp: new Date(),
      messageType: 'text' // 🆕 文本消息类型
    };

    setMessages(prev => [...prev, userMessage]);

    setIsTyping(true);

    try {
      const sessionIdToUse = historySessionId || currentTopic?.sessionId;

      if (sessionIdToUse) {
        sendMessage({
          sessionId: sessionIdToUse,
          message: message, // 发送原始消息，SocketService会再次预处理（幂等操作）
          model: getCurrentModel()
        });
      } else {
        sendMessage({
          message: message, // 发送原始消息，SocketService会再次预处理（幂等操作）
          model: getCurrentModel()
        });
      }
    } catch (error: any) {
      setIsTyping(false);
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        role: 'system',
        content: `❌ 发送失败：${error.message}`,
        timestamp: new Date(),
        messageType: 'text'
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  }, [historySessionId, currentTopic, sendMessage]);

  // 处理对话中的图片上传（多模态对话）
  const handleChatImageSelected = useCallback(async (file: File) => {
    // 打开裁剪模态窗口
    setPendingImageFile(file);
    setPendingImageUrl(URL.createObjectURL(file));
    setShowCropModal(true);
  }, []);

  // 处理裁剪后的图片发送
  const handleCroppedImageSend = useCallback(async (croppedFile: File) => {
    setShowCropModal(false);
    setIsUploading(true);

    try {
      // 1. 上传图片到服务器（使用 apiService.post 手动构建 FormData）
      if (user?.token) {
        apiService.setToken(user.token);
      }

      const formData = new FormData();
      formData.append('image', croppedFile);
      const topicIdToSend = currentTopic?.id || '';
      formData.append('topicId', topicIdToSend);
      formData.append('imageIndex', '0');

      const result = await apiService.post('/upload/chat-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 180000,
      });

      if (!result.success) {
        throw new Error(result.error?.message || '上传失败');
      }

      const uploadedImage = result.data;

      // 2. 构建服务器图片URL（与新建图片问题保持一致）
      const fullImageUrl = buildStaticUrl(uploadedImage.url);

      // 3. 添加用户消息到界面（直接使用服务器URL）
      const userMessage: Message = {
        id: `user_${Date.now()}`,
        role: 'user',
        content: '发送了图片',
        timestamp: new Date(),
        messageType: 'image',
        imageUrl: fullImageUrl
      };
      setMessages(prev => [...prev, userMessage]);

      // 4. 通过Socket发送图片消息给AI
      setIsTyping(true);
      const sessionIdToUse = historySessionId || currentTopic?.sessionId;

      if (sessionIdToUse) {
        sendMessage({
          sessionId: sessionIdToUse,
          message: '',
          model: 'gemini', // 图片消息只支持Gemini
          messageType: 'image',
          imageData: {
            url: uploadedImage.url,
            base64: uploadedImage.base64,
            filename: uploadedImage.filename,
            size: uploadedImage.size,
            mimeType: uploadedImage.mimeType
          }
        });
      }

    } catch (error: any) {
      setIsTyping(false);
      let errorDetails = error.message;
      if (error.response?.data?.message) {
        errorDetails = error.response.data.message;
      } else if (error.response?.data?.details) {
        errorDetails = error.response.data.details;
      } else if (error.request && !error.response) {
        errorDetails = '无法连接到服务器，请检查网络连接或服务器是否启动';
      }

      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        role: 'system',
        content: `❌ 图片发送失败：${errorDetails}`,
        timestamp: new Date(),
        messageType: 'text'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsUploading(false);
      // 清理临时文件
      if (pendingImageUrl) {
        URL.revokeObjectURL(pendingImageUrl);
      }
      setPendingImageFile(null);
      setPendingImageUrl(null);
    }
  }, [user?.token, currentTopic, historySessionId, sendMessage, pendingImageUrl]);

  // 更新裁剪确认处理，区分是创建话题还是对话中发送
  const handleCropConfirmUpdated = useCallback((croppedFile: File) => {
    // 如果有 currentTopic，说明是对话中的图片，直接发送到对话
    if (currentTopic) {
      handleCroppedImageSend(croppedFile);
    } else {
      // 否则是创建新话题，图片显示在右侧问题输入区
      setUploadedImage(croppedFile);
      setImagePreviewUrl(URL.createObjectURL(croppedFile));
      setImageAnalyzed(false);
      setShowCropModal(false);
      
      // 清理临时URL
      if (pendingImageUrl) {
        URL.revokeObjectURL(pendingImageUrl);
      }
      setPendingImageFile(null);
      setPendingImageUrl(null);
    }
  }, [currentTopic, handleCroppedImageSend, pendingImageUrl]);

  // 处理解题过程请求
  const handleRequestSolution = useCallback(async () => {
    if (!connected) return;

    const solutionMessage: Message = {
      id: `solution_request_${Date.now()}`,
      role: 'user',
      content: '请对学习过程进行总结',
      timestamp: new Date(),
      messageType: 'text'
    };
    setMessages(prev => [...prev, solutionMessage]);

    setIsTyping(true);

    try {
      const sessionIdToUse = historySessionId || currentTopic?.sessionId;

      if (sessionIdToUse) {
        sendMessage({
          sessionId: sessionIdToUse,
          message: '请对学习过程进行总结',
          model: getCurrentModel()
        });
      } else {
        sendMessage({
          message: '请对学习过程进行总结',
          model: getCurrentModel()
        });
      }
    } catch (error: any) {
      setIsTyping(false);
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        role: 'system',
        content: `❌ 请求解题过程失败：${error.message}`,
        timestamp: new Date(),
        messageType: 'text'
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  }, [connected, historySessionId, currentTopic, sendMessage]);

  // 移除解题按钮
  const handleRemoveSolutionButton = useCallback((messageId: string) => {
    setSolutionButtonShown(prev => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });
  }, []);

  // 获取当前应该使用的模型
  const getCurrentModel = useCallback((): AIModel => {
    // 如果是历史会话，使用话题的模型类型
    if (historySessionId) {
      return (currentTopic?.modelType as AIModel) || selectedModel;
    }
    
    // 对于新会话，优先使用localStorage中的最新值，确保获取到最新的模型选择
    const latestPreferred = getPreferredModel();
    return latestPreferred;
  }, [historySessionId, currentTopic, selectedModel]);

  // 处理模型选择变化
  const handleModelChange = useCallback((model: AIModel) => {
    if (!isModelLocked) {
      setSelectedModel(model);
      setPreferredModel(model); // 保存用户偏好到localStorage
    }
  }, [isModelLocked]);

  // 处理新问题按钮点击
  const handleNewQuestion = useCallback(() => {
    setQuestionMode('select');
    setQuestionText('');
    setUploadedImage(null);
    setImagePreviewUrl(null);
    setCurrentTopic(null);
    setImageAnalyzed(false);
    setTextAnalyzed(false);
    setHistorySessionId(null);
    setIsTopicCompleted(false);
    setSolutionButtonShown(new Set());
    setIsModelLocked(false);

    setMessages([]);

    // 清除全局会话状态
    useChatStore.getState().clearSession();
  }, []);

  // 处理问题类型选择
  const handleQuestionTypeSelect = useCallback((type: 'image' | 'text') => {
    setQuestionMode(type);
  }, []);

  // 处理图片文件上传 - 打开裁剪模态窗口
  const handleImageFileUpload = useCallback((file: File) => {
    setPendingImageFile(file);
    setPendingImageUrl(URL.createObjectURL(file));
    setShowCropModal(true);
  }, []);

  // 处理裁剪确认
  const handleCropConfirm = useCallback((croppedFile: File) => {
    setUploadedImage(croppedFile);
    setImagePreviewUrl(URL.createObjectURL(croppedFile));
    setImageAnalyzed(false);
    setShowCropModal(false);
    
    // 清理临时URL
    if (pendingImageUrl) {
      URL.revokeObjectURL(pendingImageUrl);
    }
    setPendingImageFile(null);
    setPendingImageUrl(null);
  }, [pendingImageUrl]);

  // 处理裁剪取消
  const handleCropCancel = useCallback(() => {
    setShowCropModal(false);
    
    // 清理临时URL
    if (pendingImageUrl) {
      URL.revokeObjectURL(pendingImageUrl);
    }
    setPendingImageFile(null);
    setPendingImageUrl(null);
  }, [pendingImageUrl]);

  // 获取分类名称
  const getCategoryName = useCallback((category: string): string => {
    const names: Record<string, string> = {
      'physics': '物理学',
      'chemistry': '化学',
      'math': '数学',
      'others': '其他'
    };
    return names[category] || '未知';
  }, []);

  // 处理图片上传和分析
  const handleImageUpload = useCallback(async (file: File) => {
    setIsUploading(true);
    setUploadingType('image');

    try {
      // 先上传图片
      if (user?.token) {
        apiService.setToken(user.token);
      }

      const result = await apiService.uploadFile('/upload/image', file);

      if (result.success) {
        const { imageId, sessionId, analysis, imageUrl, topicId } = result.data;

        // 构建服务器图片URL（直接使用服务器返回的URL）
        const fullImageUrl = buildStaticUrl(imageUrl);

        // 上传成功后创建用户消息（使用服务器URL）
        const userMessage: Message = {
          id: `user_${Date.now()}`,
          role: 'user',
          content: '上传了图片',
          timestamp: new Date(),
          messageType: 'image',
          imageUrl: fullImageUrl
        };
        setMessages(prev => [...prev, userMessage]);

        const newTopic: Topic = {
          id: topicId, // 使用 topicId 而不是 imageId
          sessionId,
          title: analysis.title,
          category: analysis.category,
          modelType: selectedModel,
          topicType: 'image',
          status: analysis.supported ? 'pending' : 'completed',
          imageInfo: {
            imageId,
            filename: file.name,
            fileUrl: fullImageUrl
          }
        };

        setCurrentTopic(newTopic);

        const categoryName = getCategoryName(analysis.category);
        const analysisMessage: Message = {
          id: `analysis_${Date.now()}`,
          role: 'assistant',
          content: analysis.supported
            ? `${categoryName} ✅ 支持解答

问题：
${analysis.description}  \n请选择AI模型开始对话。`
            : `${categoryName} ❌ 暂不支持

图片内容：
${analysis.description}

分类理由：
${analysis.reasoning} \n抱歉，我们目前只支持物理、化学和数学问题的解答。`,
          timestamp: new Date(),
          messageType: 'text'
        };

        setMessages(prev => [...prev, analysisMessage]);
        setImageAnalyzed(true);

        // 显示会员续费提醒（如果有）
        if (result.data.warningMessage) {
          setMembershipModal({
            isOpen: true,
            message: result.data.warningMessage,
            type: 'warning'
          });
        }
      } else {
        // 学习币不足等错误：apiService.handleError 直接返回服务端 JSON，不会抛异常
        const resultAny = result as any;
        const errorMsg = resultAny.message || result.error?.message || '图片分析失败';
        
        if (errorMsg.includes('学习币不足') || errorMsg.includes('会员已过期') || errorMsg.includes('已转换为按次付费')) {
          setMembershipModal({
            isOpen: true,
            message: errorMsg,
            type: 'error',
            qrCodeUrl: resultAny.qrCodeUrl,
            alreadyBound: resultAny.alreadyBound
          });
        }

        throw new Error(errorMsg);
      }
    } catch (error: any) {
      let errorDetails = error.message;
      if (error.response?.data?.message) {
        errorDetails = error.response.data.message;
      } else if (error.response?.data?.details) {
        errorDetails = error.response.data.details;
      } else if (error.request && !error.response) {
        errorDetails = '无法连接到服务器，请检查网络连接或服务器是否启动';
      }

      // 会员过期或学习币不足：用模态窗口提示（仅当 else 分支未设置时）
      if (errorDetails.includes('学习币不足') || errorDetails.includes('会员已过期') || errorDetails.includes('已转换为按次付费')) {
        const responseData = error.response?.data;
        if (responseData?.qrCodeUrl) {
          setMembershipModal({
            isOpen: true,
            message: errorDetails,
            type: 'error',
            qrCodeUrl: responseData.qrCodeUrl,
            alreadyBound: responseData.alreadyBound
          });
        }
      }

      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        role: 'system',
        content: `❌ 图片上传失败：${errorDetails}`,
        timestamp: new Date(),
        messageType: 'text'
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsUploading(false);
      setUploadingType(null);
    }
  }, [user?.token, selectedModel, getCategoryName]);

  // 处理右侧文本问题
  const handleTextQuestionFromRight = useCallback(async (question: string) => {
    setIsUploading(true);
    setUploadingType('text');

    try {
      // 预处理LaTeX表达式（添加定界符）
      const processedQuestion = preprocessUserLatex(question);
      
      const userMessage: Message = {
        id: `user_${Date.now()}`,
        role: 'user',
        content: processedQuestion, // 直接使用预处理后的问题，与历史加载保持一致
        timestamp: new Date(),
        messageType: 'text'
      };
      setMessages(prev => [...prev, userMessage]);

      if (user?.token) {
        apiService.setToken(user.token);
      }

      // 发送预处理后的问题到后端（Gemini分析可能较慢，设置3分钟超时）
      const result = await apiService.post('/upload/text', { question: processedQuestion }, { timeout: 180000 });

      if (result.success) {
        const { topicId, sessionId, analysis } = result.data;

        const newTopic: Topic = {
          id: topicId,
          sessionId,
          title: analysis.title,
          category: analysis.category,
          modelType: selectedModel,
          topicType: 'text',
          status: analysis.supported ? 'pending' : 'completed',
        };

        setCurrentTopic(newTopic);

        const categoryName = getCategoryName(analysis.category);
        const analysisMessage: Message = {
          id: `analysis_${Date.now()}`,
          role: 'assistant',
          content: analysis.supported
            ? `${categoryName} ✅ 支持解答

问题：
${analysis.description} \n请选择AI模型开始对话。`
            : `${categoryName} ❌ 暂不支持

问题：
${analysis.description}

抱歉，我们目前只支持物理、化学和数学问题的解答。`,
          timestamp: new Date(),
          messageType: 'text'
        };

        setMessages(prev => [...prev, analysisMessage]);
        setTextAnalyzed(true);
        setQuestionText('');

        // 显示会员续费提醒（如果有）
        if (result.data.warningMessage) {
          setMembershipModal({
            isOpen: true,
            message: result.data.warningMessage,
            type: 'warning'
          });
        }
      } else {
        // 学习币不足等错误：apiService.handleError 直接返回服务端 JSON，不会抛异常
        const resultAny = result as any;
        const errorMsg = resultAny.message || result.error?.message || '问题分析失败';
        
        if (errorMsg.includes('学习币不足') || errorMsg.includes('会员已过期') || errorMsg.includes('已转换为按次付费')) {
          setMembershipModal({
            isOpen: true,
            message: errorMsg,
            type: 'error',
            qrCodeUrl: resultAny.qrCodeUrl,
            alreadyBound: resultAny.alreadyBound
          });
        }

        throw new Error(errorMsg);
      }
    } catch (error: any) {
      let errorDetails = error.message;
      if (error.response?.data?.message) {
        errorDetails = error.response.data.message;
      } else if (error.response?.data?.details) {
        errorDetails = error.response.data.details;
      } else if (error.request && !error.response) {
        errorDetails = '无法连接到服务器，请检查网络连接或服务器是否启动';
      }

      // 会员过期或学习币不足：用模态窗口提示（仅当 else 分支未设置时）
      if (errorDetails.includes('学习币不足') || errorDetails.includes('会员已过期') || errorDetails.includes('已转换为按次付费')) {
        // 只有来自 axios 的真实 HTTP 错误才有 response.data（else 分支抛出的 Error 没有）
        const responseData = error.response?.data;
        if (responseData?.qrCodeUrl) {
          setMembershipModal({
            isOpen: true,
            message: errorDetails,
            type: 'error',
            qrCodeUrl: responseData.qrCodeUrl,
            alreadyBound: responseData.alreadyBound
          });
        }
        // 如果 responseData 没有 qrCodeUrl，说明 else 分支已经设置过 modal，不再覆盖
      }

      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        role: 'system',
        content: `❌ 问题分析失败：${errorDetails}`,
        timestamp: new Date(),
        messageType: 'text'
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsUploading(false);
      setUploadingType(null);
    }
  }, [user?.token, selectedModel, getCategoryName]);

  // 处理右侧发送按钮
  const handleRightSend = useCallback(async () => {
    if (questionMode === 'image' && uploadedImage) {
      await handleImageUpload(uploadedImage);
    } else if (questionMode === 'text' && questionText.trim()) {
      await handleTextQuestionFromRight(questionText);
    }
  }, [questionMode, uploadedImage, questionText, handleImageUpload, handleTextQuestionFromRight]);
  // 检查是否需要加载历史会话
  useEffect(() => {
    const state = location.state as any;

    if (state?.loadHistoryTopicId && state?.fromHistory) {
      const loadHistory = async () => {
        setLoadingHistory(true);

        try {
          if (user?.token) {
            apiService.setToken(user.token);
          }

          const result = await apiService.post(`/history/topics/${state.loadHistoryTopicId}/activate`, {});

          if (result.success) {
            const { sessionId, topicInfo } = result.data;
            setHistorySessionId(sessionId);

            setMessages([{
              id: 'loading_history',
              role: 'system',
              content: `🔄 正在加载历史会话：${topicInfo.title}...`,
              timestamp: new Date()
            }]);

            const detailResult = await apiService.get(`/history/topics/${state.loadHistoryTopicId}`);

            if (detailResult.success) {
              const { topic, messages: historyMessages } = detailResult.data;

              const restoredMessages: Message[] = (historyMessages || [])
                .sort((a: any, b: any) => (a.messageOrder || a.message_order) - (b.messageOrder || b.message_order))
                .map((msg: any) => {
                  // 🆕 解析图片URL（如果消息包含图片URL）
                  let imageUrl: string | undefined;
                  let messageContent = msg.content;
                  let messageType: 'text' | 'image' = 'text';
                  
                  // 检查content是否包含图片URL标记
                  if (messageContent && messageContent.startsWith('[图片URL]')) {
                    messageType = 'image';
                    
                    // 从content中提取图片URL
                    const urlMatch = messageContent.match(/\[图片URL\][\s\n]+(.+?)(?:\d{2}:\d{2}:\d{2})?$/s);
                    if (urlMatch) {
                      const rawImageUrl = urlMatch[1].trim();
                      // 使用buildStaticUrl构建完整URL
                      imageUrl = buildStaticUrl(rawImageUrl);
                      // 对于图片消息，content应该为空（只显示图片）
                      messageContent = '';
                    }
                  }

                  return {
                    id: msg.id,
                    role: msg.role,
                    content: messageContent,
                    timestamp: new Date(msg.created_at || msg.createdAt),
                    topicId: topic.id,
                    model: msg.model_used || msg.modelUsed,
                    tokenCount: msg.token_count || msg.tokenCount,
                    responseTime: msg.response_time_ms || msg.responseTimeMs,
                    // 🆕 多模态支持
                    messageType: messageType,
                    imageUrl: imageUrl,
                    attachments: msg.attachments ? JSON.parse(msg.attachments) : undefined
                  } as Message;
                });

              const initialMessages: Message[] = [];

              if (topic.initialQuestion) {
                const initialQuestionMessage: Message = {
                  id: `initial_question_${Date.now()}`,
                  role: 'user',
                  content: topic.initialQuestion,
                  timestamp: new Date(topic.createdAt),
                  topicId: topic.id,
                  messageType: 'text'
                };
                initialMessages.push(initialQuestionMessage);
              }

              const successMessage: Message = {
                id: `restored_${Date.now()}`,
                role: 'system',
                content: `✅ 历史会话已恢复，您可以继续对话`,
                timestamp: new Date(),
                messageType: 'text'
              };

              const finalMessages = [...initialMessages, ...restoredMessages, successMessage];
              setMessages(finalMessages);

              setCurrentTopic({
                id: topic.id,
                sessionId: sessionId,
                title: topic.title,
                category: topic.category,
                modelType: topicInfo.modelType,
                topicType: topic.type,
                status: topic.status || 'active',
                ...(topic.type === 'image' && topicInfo.imageInfo ? {
                  imageInfo: topicInfo.imageInfo
                } : {}),
                ...(topic.type === 'text' ? {
                  initialQuestion: topic.initialQuestion
                } : {})
              });

              // 同步selectedModel为历史会话使用的模型
              if (topicInfo.modelType && (topicInfo.modelType === 'gemini' || topicInfo.modelType === 'deepseek')) {
                setSelectedModel(topicInfo.modelType);
              }

              if (topic.originalContent) {
                if (topic.originalContent.type === 'image' && topic.originalContent.imageInfo) {
                  setQuestionMode('image');
                  const imageUrl = topic.originalContent.imageInfo.imageUrl;
                  const fullImageUrl = buildStaticUrl(imageUrl);
                  setImagePreviewUrl(fullImageUrl);
                } else if (topic.originalContent.type === 'text' && topic.originalContent.textContent) {
                  setQuestionMode('text');
                  setQuestionText(topic.originalContent.textContent);
                  setTextAnalyzed(true);
                }
              }

              const isCompleted = topic.status === 'completed';
              setIsTopicCompleted(isCompleted);
              setTopicCompletionStatus(prev => ({ ...prev, [topic.id]: isCompleted }));
              setIsModelLocked(true);
              
              // 如果是已完成的话题，显示提示，不显示总结按钮
              if (isCompleted) {
                const completedNoticeMessage: Message = {
                  id: `completed_notice_${Date.now()}`,
                  role: 'system',
                  content: '💬 对话已完成，请提出新问题',
                  timestamp: new Date(),
                  messageType: 'text'
                };
                setMessages(prev => [...prev, completedNoticeMessage]);
              }
            } else {
              throw new Error(detailResult.error?.message || '获取历史消息失败');
            }
          } else {
            throw new Error(result.error?.message || '激活历史会话失败');
          }
        } catch (error: any) {
          console.error('Load history session failed:', error);
          let errorMsg = error.message;
          if (error.response?.data?.message) {
            errorMsg = error.response.data.message;
          }

          const errorMessage: Message = {
            id: `error_${Date.now()}`,
            role: 'system',
            content: `❌ 加载历史会话失败：${errorMsg}`,
            timestamp: new Date(),
            messageType: 'text'
          };

          setMessages(prev => [...prev, errorMessage]);
        } finally {
          setLoadingHistory(false);
        }
      };

      loadHistory();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, user?.token]);

  // 页面加载时检查当前会话状态
  useEffect(() => {
    const checkSessionStatus = async () => {
      if (!user?.token || !currentTopic?.id) return;

      try {
        if (user?.token) {
          apiService.setToken(user.token);
        }
        
        // 获取当前话题的最新状态
        const result = await apiService.get(`/history/topics/${currentTopic.id}`);
        
        if (result.success && result.data) {
          const topicData = result.data;
          
          // 如果话题状态是已断开，更新本地状态
          if (topicData.status === 'disconnected' && currentTopic.status !== 'disconnected') {
            setCurrentTopic(prev => {
              if (prev) {
                return { ...prev, status: 'disconnected' };
              }
              return prev;
            });
            
            // 显示断开消息
            const disconnectMessage: Message = {
              id: `status_check_${Date.now()}`,
              role: 'system',
              content: '🔌 检测到会话已断开，请开始新的对话',
              timestamp: new Date(),
              messageType: 'text'
            };
            setMessages(prev => [...prev, disconnectMessage]);
          }
        }
      } catch (error) {
        console.warn('Failed to check session status:', error);
      }
    };

    // 延迟检查，确保组件已完全加载
    const timer = setTimeout(checkSessionStatus, 1000);
    return () => clearTimeout(timer);
  }, [currentTopic?.id, currentTopic?.status, user?.token]);

  return (
    <div className="fixed-layout-container bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 mobile-scrollable">
      <div className="h-full max-w-7xl mx-auto p-3 lg:p-5 mobile-safe-area">
        <div className="grid grid-cols-1 lg:grid-cols-8 gap-3 lg:gap-5 h-auto lg:h-full">

          {/* 左侧：聊天区域 - 使用分离的组件 */}
          <div className="col-span-1 lg:col-span-5 bg-white dark:bg-gray-800 rounded-2xl shadow-xl flex flex-col lg:h-full overflow-hidden">
            {/* 消息区域 - 占据大部分空间，可滚动 */}
            <div className="flex-1 overflow-hidden">
              <ChatMessageArea
                messages={messages}
                isTyping={isTyping}
                isUploading={isUploading}
                loadingHistory={loadingHistory}
                solutionButtonShown={solutionButtonShown}
                connected={connected}
                currentTopic={currentTopic}
                historySessionId={historySessionId}
                onRequestSolution={handleRequestSolution}
                onRemoveSolutionButton={handleRemoveSolutionButton}
              />
            </div>

            {/* 输入区域 - 固定在底部，不会被挤压 */}
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
              <ChatInputArea
                connected={connected}
                isTyping={isTyping}
                isUploading={isUploading}
                loadingHistory={loadingHistory}
                isTopicCompleted={isTopicCompleted}
                currentTopic={currentTopic}
                selectedModel={selectedModel}
                onSendMessage={handleSendMessage}
                onImageSelected={handleChatImageSelected}
              />
              
            </div>
          </div>

          {/* 右侧：问题输入区域 - 保持在主组件中 */}
          <div className="col-span-1 lg:col-span-3 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 lg:p-6 flex flex-col mobile-input-area lg:h-full lg:max-h-screen">
            {/* 标题和新问题按钮 */}
            <div className="flex items-center justify-between mb-6 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                📝 问题输入
              </h2>

              <button
                onClick={handleNewQuestion}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                + 新问题
              </button>
            </div>

            {/* 初始状态：显示开始提示 */}
            {questionMode === 'none' && (
              <div className="flex-1 flex items-center justify-center text-center text-gray-500 dark:text-gray-400">
                <div>
                  <div className="text-4xl mb-3">🤔</div>
                  <div className="text-sm">点击右上角"+ 新问题"开始</div>
                </div>
              </div>
            )}

            {/* 问题类型选择 */}
            {questionMode === 'select' && (
              <div className="flex-1 flex flex-col items-center justify-center space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  选择问题类型
                </h3>

                <button
                  onClick={() => handleQuestionTypeSelect('image')}
                  className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                >
                  <div className="text-3xl mb-2">📸</div>
                  <div className="font-medium text-gray-900 dark:text-white">图片问题</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">上传图片进行分析</div>
                </button>

                <button
                  onClick={() => handleQuestionTypeSelect('text')}
                  className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all"
                >
                  <div className="text-3xl mb-2">💬</div>
                  <div className="font-medium text-gray-900 dark:text-white">文本问题</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">输入文字问题</div>
                </button>
              </div>
            )}

            {/* 图片上传模式 */}
            {questionMode === 'image' && (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 mb-4 overflow-y-auto">
                  {!imagePreviewUrl ? (
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center h-full flex flex-col items-center justify-center">
                      <div className="text-4xl mb-4">📸</div>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {historySessionId ? "历史图片（只读）" : "选择图片文件或拍照上传"}
                      </p>
                      {!historySessionId && (
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageFileUpload(file);
                          }}
                          className="hidden"
                          id="image-upload"
                        />
                      )}
                      {!historySessionId && (
                        <label
                          htmlFor="image-upload"
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                        >
                          获取图片
                        </label>
                      )}
                    </div>
                  ) : (
                    <div className="relative flex items-center justify-center p-2">
                      <img
                        src={imagePreviewUrl}
                        alt="上传的图片"
                        className="max-w-full h-auto object-contain rounded-xl"
                      />
                      {historySessionId && (
                        <div className="absolute top-2 right-2 bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          历史图片
                        </div>
                      )}
                      {!historySessionId && !imageAnalyzed && !uploadingType && (
                        <button
                          onClick={() => {
                            setUploadedImage(null);
                            setImagePreviewUrl(null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm hover:bg-red-600 transition-colors shadow-lg"
                          title="删除图片"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {/* 分析完成后显示AI模型选择 */}
                {imageAnalyzed && !historySessionId && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                      {isModelLocked ? '当前对话模型' : '选择对话AI模型'}
                    </label>
                    <ModelSelector
                      selectedModel={selectedModel}
                      onModelChange={handleModelChange}
                      isLocked={isModelLocked}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                      {isModelLocked ? '模型已锁定，整个对话将使用此模型' : '选择用于对话的AI模型'}
                    </div>
                  </div>
                )}

                {/* 历史会话显示当前模型 */}
                {historySessionId && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                      当前对话模型
                    </label>
                    <ModelSelector
                      selectedModel={getCurrentModel() as AIModel}
                      onModelChange={() => {}}
                      isLocked={true}
                    />
                  </div>
                )}

                {/* 发送按钮 */}
                <button
                  onClick={handleRightSend}
                  disabled={!uploadedImage || isUploading || imageAnalyzed}
                  className="w-full text-white py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ backgroundColor: '#07c160' }}
                  onMouseEnter={(e) => !(!uploadedImage || isUploading || imageAnalyzed) && (e.currentTarget.style.backgroundColor = '#06ad56')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                >
                  {isUploading ? '分析中...' : imageAnalyzed ? '已分析' : '开始分析'}
                </button>
              </div>
            )}
            {/* 文本输入模式 */}
            {questionMode === 'text' && (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 mb-4 relative">
                  <UnifiedMathInput
                    value={questionText}
                    onChange={setQuestionText}
                    placeholder={historySessionId ? "历史问题（只读）" : "请输入您的问题..."}
                    rows={8}
                    readOnly={historySessionId !== null}
                    className="h-full"
                  />
                  {historySessionId && questionText && (
                    <div className="absolute top-2 right-12 bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs z-10">
                      历史问题
                    </div>
                  )}
                </div>

                {/* 分析完成后显示AI模型选择 */}
                {textAnalyzed && !historySessionId && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                      {isModelLocked ? '当前对话模型' : '选择对话AI模型'}
                    </label>
                    <ModelSelector
                      selectedModel={selectedModel}
                      onModelChange={handleModelChange}
                      isLocked={isModelLocked}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                      {isModelLocked ? '模型已锁定，整个对话将使用此模型' : '选择用于对话的AI模型'}
                    </div>
                  </div>
                )}

                {/* 历史会话显示当前模型 */}
                {historySessionId && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                      当前对话模型
                    </label>
                    <ModelSelector
                      selectedModel={getCurrentModel() as AIModel}
                      onModelChange={() => {}}
                      isLocked={true}
                    />
                  </div>
                )}

                {/* 发送按钮 */}
<button
                  onClick={handleRightSend}
                  disabled={!questionText.trim() || isUploading || textAnalyzed}
                  className="w-full text-white py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ backgroundColor: '#07c160' }}
                  onMouseEnter={(e) => !(!questionText.trim() || isUploading || textAnalyzed) && (e.currentTarget.style.backgroundColor = '#06ad56')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                >
                  {isUploading ? '分析中...' : textAnalyzed ? '已分析' : '开始分析'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 评分对话框 */}
      {showRatingDialog && completedTopicId && (
        <TopicRatingDialog
          isOpen={showRatingDialog}
          topicId={completedTopicId}
          topicTitle={currentTopic?.title}
          completionReason={completionReason}
          onSubmit={(rating, feedback) => {
            submitRating({
              topicId: completedTopicId,
              rating,
              feedback
            });
            setShowRatingDialog(false);
            setCompletedTopicId(null);
            setCompletionReason(undefined);
          }}
          onSkip={() => {
            submitRating({
              topicId: completedTopicId,
              skipped: true
            });
            setShowRatingDialog(false);
            setCompletedTopicId(null);
            setCompletionReason(undefined);
          }}
          onClose={() => {
            setShowRatingDialog(false);
            setCompletedTopicId(null);
            setCompletionReason(undefined);
          }}
        />
      )}

      {/* 图片裁剪模态窗口 */}
      {showCropModal && pendingImageFile && pendingImageUrl && (
        <ImageCropModal
          imageFile={pendingImageFile}
          imageUrl={pendingImageUrl}
          onConfirm={handleCropConfirmUpdated}
          onCancel={handleCropCancel}
        />
      )}

      {/* 会员状态提醒模态窗口 */}
      <MembershipWarningModal
        isOpen={membershipModal.isOpen}
        message={membershipModal.message}
        type={membershipModal.type}
        onClose={() => setMembershipModal({ ...membershipModal, isOpen: false })}
        qrCodeUrl={membershipModal.qrCodeUrl}
        alreadyBound={membershipModal.alreadyBound}
      />
    </div>
  );
};

export { ChatInterface as default };